import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Target, Trophy, Clock, RefreshCw, AlertCircle } from 'lucide-react';
import { budgetService, type Budget } from '../../services/budgetService';

const fmtEUR = (n: number) =>
  n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 });

const fmtDate = (iso: string | null | undefined) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return null; }
};

/**
 * Card "Il mio Budget" per `/area-clienti`.
 *
 * Mostra 3 varianti in base a ``budget.status``:
 *   - **pre-target**       : motivazionale, "Ti mancano €X per sbloccare Y%"
 *   - **target-reached**   : celebrativa, "🎉 Sconto Y% attivo!"
 *   - **expired**          : informativa, riepilogo periodo terminato
 *
 * Se ``budget.enabled=false`` o l'utente non è collegato a un cliente ERP →
 * la card NON viene renderizzata (return null).
 */
export function BudgetCard() {
  const [budget, setBudget] = useState<Budget | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (force = false) => {
    try {
      const data = await budgetService.getMine(force);
      setBudget(data);
    } catch {
      setBudget(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(false); }, []);

  if (loading) {
    return (
      <div className="rounded-xl border bg-white p-5 shadow-sm animate-pulse" data-testid="budget-card-loading">
        <div className="h-4 w-32 bg-secondary rounded mb-3" />
        <div className="h-3 w-48 bg-secondary rounded mb-4" />
        <div className="h-2 w-full bg-secondary rounded" />
      </div>
    );
  }

  if (!budget || !budget.enabled) return null;

  // ── Variante "expired" ──
  if (budget.status === 'expired') {
    return (
      <ExpiredCard budget={budget} onRefresh={() => { setRefreshing(true); load(true); }} refreshing={refreshing} />
    );
  }

  // ── Variante "target-reached" ──
  if (budget.status === 'target-reached') {
    return (
      <TargetReachedCard budget={budget} onRefresh={() => { setRefreshing(true); load(true); }} refreshing={refreshing} />
    );
  }

  // ── Variante "pre-target" (default) ──
  return (
    <PreTargetCard budget={budget} onRefresh={() => { setRefreshing(true); load(true); }} refreshing={refreshing} />
  );
}

// ── Sub-componenti ──────────────────────────────────────────────────────

interface SubProps { budget: Budget; onRefresh: () => void; refreshing: boolean; }

function PreTargetCard({ budget, onRefresh, refreshing }: SubProps) {
  const scadenza = fmtDate(budget.dataScadenza);
  const giorni = budget.giorniMancanti;
  return (
    <div
      className="rounded-xl border bg-gradient-to-br from-blue-50 via-white to-white p-5 shadow-sm overflow-hidden relative"
      data-testid="budget-card-pre-target"
    >
      <CardHeader
        icon={<Target className="w-5 h-5 text-[hsl(214,58%,41%)]" />}
        title="Il tuo Budget"
        badge={`Periodo ${budget.mesiPeriodo} mesi`}
        accent="text-[hsl(214,58%,41%)]"
        onRefresh={onRefresh}
        refreshing={refreshing}
      />
      <div className="mt-2 text-sm text-muted-foreground">
        Ti mancano{' '}
        <span className="font-bold text-foreground tabular-nums" data-testid="budget-target-mancante">
          {fmtEUR(budget.targetMancante)}
        </span>{' '}
        per sbloccare lo sconto{' '}
        <span className="font-bold text-[hsl(214,58%,41%)]">{budget.percentuale}%</span>.
      </div>
      <ProgressBar value={budget.progressoVersoTarget} variant="blue" />
      <div className="text-xs text-muted-foreground tabular-nums">
        {fmtEUR(budget.fatturatoCorrente)} / {fmtEUR(budget.valore)}
        <span className="ml-1 text-[10px]">({budget.progressoVersoTarget.toFixed(0)}%)</span>
      </div>
      {scadenza && giorni !== null && (
        <div className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
          <Clock className="w-3.5 h-3.5" />
          Periodo termina tra <span className="font-medium text-foreground">{giorni} giorni</span> ({scadenza})
        </div>
      )}
      <p className="mt-3 text-xs text-muted-foreground italic">
        Raggiunto il valore del budget verrà emessa una nota di credito con importo valido per i tuoi futuri acquisti.
      </p>
      <Link
        to="/prodotti"
        className="mt-4 inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-[hsl(214,58%,41%)] text-white text-sm font-medium hover:bg-[hsl(214,58%,46%)] transition-colors"
        data-testid="budget-cta-shopping"
      >
        Vai allo shopping →
      </Link>
    </div>
  );
}

function TargetReachedCard({ budget, onRefresh, refreshing }: SubProps) {
  const scadenza = fmtDate(budget.dataScadenza);
  return (
    <div
      className="rounded-xl border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 via-white to-emerald-50/30 p-5 shadow-md overflow-hidden relative"
      data-testid="budget-card-target-reached"
    >
      <CardHeader
        icon={<Trophy className="w-5 h-5 text-emerald-600" />}
        title={`Sconto ${budget.percentuale}% attivo!`}
        badge={`Periodo ${budget.mesiPeriodo} mesi`}
        accent="text-emerald-700"
        onRefresh={onRefresh}
        refreshing={refreshing}
      />
      <p className="mt-2 text-sm text-emerald-900">
        Hai raggiunto il target di <span className="font-semibold tabular-nums">{fmtEUR(budget.valore)}</span>.
        Ogni acquisto è scontato del <span className="font-semibold">{budget.percentuale}%</span> sulla porzione che eccede la soglia.
      </p>
      <ProgressBar value={100} variant="emerald" />
      <div className="text-xs text-emerald-800 tabular-nums">
        {fmtEUR(budget.fatturatoCorrente)} fatturato (
        <span className="font-semibold">+{fmtEUR(budget.eccedenzaSoglia)} con sconto</span>)
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
        <div className="rounded-md bg-white/60 border border-emerald-200 p-2.5">
          <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Risparmio cumulato</div>
          <div className="text-base font-bold text-emerald-700 tabular-nums">{fmtEUR(budget.risparmioStimato)}</div>
        </div>
        {scadenza && budget.giorniMancanti !== null && (
          <div className="rounded-md bg-white/60 border border-emerald-200 p-2.5">
            <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Sconto attivo per</div>
            <div className="text-base font-bold text-emerald-700">{budget.giorniMancanti} giorni</div>
          </div>
        )}
      </div>
      <Link
        to="/prodotti"
        className="mt-4 inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 transition-colors"
        data-testid="budget-cta-continue-saving"
      >
        Continua a risparmiare →
      </Link>
    </div>
  );
}

function ExpiredCard({ budget, onRefresh, refreshing }: SubProps) {
  const scadenza = fmtDate(budget.dataScadenza);
  const reachedTarget = budget.fatturatoCorrente >= budget.valore;
  return (
    <div
      className="rounded-xl border bg-gradient-to-br from-gray-50 to-white p-5 shadow-sm"
      data-testid="budget-card-expired"
    >
      <CardHeader
        icon={<AlertCircle className="w-5 h-5 text-gray-500" />}
        title="Periodo budget terminato"
        badge={`Periodo ${budget.mesiPeriodo} mesi`}
        accent="text-gray-600"
        onRefresh={onRefresh}
        refreshing={refreshing}
      />
      <p className="mt-2 text-sm text-muted-foreground">
        Il periodo è terminato {scadenza ? `il ${scadenza}` : 'da poco'}. Riepilogo:
      </p>
      <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
        <div className="rounded-md bg-white border p-2.5">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Fatturato</div>
          <div className="text-sm font-bold tabular-nums">{fmtEUR(budget.fatturatoCorrente)}</div>
          <div className="text-[10px] text-muted-foreground">su {fmtEUR(budget.valore)}</div>
        </div>
        <div className="rounded-md bg-white border p-2.5">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Status</div>
          <div className={`text-sm font-bold ${reachedTarget ? 'text-emerald-700' : 'text-amber-700'}`}>
            {reachedTarget ? 'Target raggiunto ✓' : 'Sotto soglia'}
          </div>
          {reachedTarget && (
            <div className="text-[10px] text-emerald-700">Risparmio: {fmtEUR(budget.risparmioStimato)}</div>
          )}
        </div>
      </div>
      <p className="mt-3 text-xs text-muted-foreground italic">
        Il nuovo periodo partirà non appena il tuo agente lo attiverà.
      </p>
    </div>
  );
}

// ── Bits ────────────────────────────────────────────────────────────────

function CardHeader({
  icon, title, badge, accent, onRefresh, refreshing,
}: {
  icon: React.ReactNode; title: string; badge: string;
  accent: string; onRefresh: () => void; refreshing: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-2">
      <div className="flex items-center gap-2">
        {icon}
        <h3 className={`text-base font-semibold ${accent}`}>{title}</h3>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-secondary text-muted-foreground border">
          {badge}
        </span>
        <button
          onClick={onRefresh}
          disabled={refreshing}
          className="tap flex items-center justify-center p-1.5 rounded-md text-muted-foreground hover:bg-secondary/60 transition-colors disabled:opacity-50"
          title="Aggiorna dai dati ERP"
          aria-label="Aggiorna dai dati ERP"
          data-testid="budget-refresh-btn"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>
    </div>
  );
}

function ProgressBar({ value, variant }: { value: number; variant: 'blue' | 'emerald' }) {
  const pct = Math.min(100, Math.max(0, value));
  const color = variant === 'emerald' ? 'bg-emerald-500' : 'bg-[hsl(214,58%,41%)]';
  return (
    <div className="mt-3 mb-1 h-2.5 w-full rounded-full bg-secondary/60 overflow-hidden" data-testid="budget-progress-bar">
      <div
        className={`h-full ${color} transition-all duration-500`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}
